import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';
import { Plus, Edit, Trash2, Users, Search, Loader2 } from 'lucide-react';

const Clients = () => {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    address: '',
    phone: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', company: '', address: '', phone: '' });
    setEditingClient(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (client) => {
    setFormData({
      name: client.name,
      email: client.email || '',
      company: client.company || '',
      address: client.address || '',
      phone: client.phone || ''
    });
    setEditingClient(client);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    if (!formData.email && !formData.phone) {
      toast.error('A client needs at least an email address or a phone number');
      return;
    }

    setSaving(true);
    const payload = {
      name: formData.name.trim(),
      email: (formData.email || '').trim() || null,
      company: (formData.company || '').trim() || null,
      address: (formData.address || '').trim() || null,
      phone: (formData.phone || '').trim() || null
    };
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.client_id}`, payload);
        toast.success('Client updated');
      } else {
        await api.post('/clients', payload);
        toast.success('Client created');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save client'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (client.company && client.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Layout>
      <div className="p-6 lg:p-12 max-w-7xl mx-auto space-y-8" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight">Clients</h1>
            <p className="text-muted-foreground mt-1">Manage your client database</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 shadow-lg shadow-primary/20" onClick={openCreateDialog} data-testid="add-client-btn">
                <Plus className="w-4 h-4" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
                <DialogDescription>
                  {editingClient ? 'Update client information' : 'Add a new client to invoice'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      data-testid="client-email-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Required unless phone is provided
                    </p>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Acme Inc."
                      data-testid="client-company-input"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 234 567 8900"
                      data-testid="client-phone-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Required unless email is provided
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Main St, City, Country"
                      data-testid="client-address-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="save-client-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingClient ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-muted-foreground">Loading clients...</div>
            ) : filteredClients.length === 0 ? (
              searchTerm ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-medium text-lg mb-2">No clients found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your search
                  </p>
                </div>
              ) : (
                <div className="m-6 rounded-2xl bg-accent text-accent-foreground p-12 text-center border-2 border-foreground/80">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-accent-foreground/10 flex items-center justify-center">
                    <Users className="w-8 h-8" />
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] opacity-80 mb-3 block">
                    ✱ &nbsp; Your client list is empty
                  </span>
                  <h3 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-3 leading-tight">
                    Add your first client.
                  </h3>
                  <p className="opacity-80 mb-8 max-w-md mx-auto">
                    Save the folks you work with once, invoice them in a single click forever after.
                  </p>
                  <Button
                    onClick={openCreateDialog}
                    size="lg"
                    className="rounded-full px-8 bg-foreground text-background hover:bg-foreground/90"
                  >
                    Add Client
                  </Button>
                </div>
              )
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Contact</TableHead>
                    <TableHead className="hidden sm:table-cell">Company</TableHead>
                    <TableHead className="hidden sm:table-cell">Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client.client_id} data-testid={`client-row-${client.client_id}`}>
                      <TableCell className="font-medium">
                        <div>
                          <span>{client.name}</span>
                          <p className="font-normal text-xs text-muted-foreground sm:hidden">{client.email || client.phone || 'No contact'}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {client.email || (
                          <span className="text-muted-foreground">
                            {client.phone || 'No contact'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">{client.company || '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell">{formatDate(client.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(client)}
                          data-testid={`edit-client-${client.client_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(client.client_id)}
                          data-testid={`delete-client-${client.client_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Clients;
